import React, { useEffect, useRef, useState } from "react";
import {
  NavLink,
  Outlet,
  useParams,
  useNavigate,
} from "react-router-dom";
import UploadCandidateModal from "../../utils/UploadCandidateModal";
import { Alert, Snackbar } from "@mui/material";
import {
  fetchCandidatesByJobId,
  uploadCsvCandidateFile,
  uploadPdfCandidateFile,
} from "../../Redux/ActionCreators/candidateAction";
import { fetchJobById } from "../../Redux/ActionCreators/jobsAction";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";

const JobTabs = () => {
  const navigate = useNavigate();
  const csvInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const dispatch = useDispatch();
  const { jobId } = useParams();

  const job = useSelector((state) => state.jobs.latestJob);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [titleLoading, setTitleLoading] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackSeverity, setSnackSeverity] = useState("error");
  const [file, setFile] = useState({ type: "", data: [] });
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0,
  });

  useEffect(() => {
    const fetchJobData = async () => {
      if (jobId) {
        setTitleLoading(true);
        await dispatch(fetchJobById(jobId));
        setTitleLoading(false);
      }
    };
    fetchJobData();
  }, [dispatch, jobId]);

  const resetUploadModal = () => {
    setShowUploadModal(false);
    setFile({ type: "", data: [] });
    if (csvInputRef.current) csvInputRef.current.value = null;
    if (pdfInputRef.current) pdfInputRef.current.value = null;
  };

  const downloadSampleCsv = () => {
    const headers = ["name", "email", "phonenumber"];
    const csvContent = [headers].map((r) => r.join(",")).join("\n");
    // use data URI
    const dataUri =
      "data:text/csv;charset=utf-8," + encodeURIComponent(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", dataUri);
    link.setAttribute("download", "sample.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const snackClosed = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackOpen(false);
  };

  const showSnack = (message, severity = "error") => {
    setSnackMessage(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  const MAX_FILES = 20;

  const handleCsvClick = () => {
    csvInputRef.current.click();
  };

  const handlePdfClick = () => {
    pdfInputRef.current.click();
  };

  const handleCsvUpload = (e) => {
    const f = e.target.files[0];
    if (!f) return;

    const allowedMimeTypes = [
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    const allowedExtensions = [".csv", ".xls", ".xlsx"];
    const fileExtension = f.name
      .substring(f.name.lastIndexOf("."))
      .toLowerCase();

    if (
      !allowedMimeTypes.includes(f.type) &&
      !allowedExtensions.includes(fileExtension)
    ) {
      showSnack("Only CSV or Excel files are allowed.", "error");
      e.target.value = null;
      return;
    }

    if (1 > MAX_FILES) {
      showSnack("You can upload a maximum of 20 files.", "error");
      e.target.value = null;
      return;
    }

    if (pdfInputRef.current) {
      pdfInputRef.current.value = null;
    }

    setFile({ type: "csv", data: [f] });
  };

  const handlePdfUpload = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const allowedMimeTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    const allowedExtensions = [".pdf", ".docx"];

    if (files.length > MAX_FILES) {
      showSnack("You can upload a maximum of 20 files.", "error");
      e.target.value = null;
      return;
    }

    const invalidFile = files.find((file) => {
      const ext = file.name
        .substring(file.name.lastIndexOf("."))
        .toLowerCase();

      return (
        !allowedMimeTypes.includes(file.type) &&
        !allowedExtensions.includes(ext)
      );
    });

    if (invalidFile) {
      showSnack("Only PDF or DOCX files are allowed.", "error");
      e.target.value = null;
      return;
    }

    if (csvInputRef.current) {
      csvInputRef.current.value = null;
    }

    setFile({ type: "pdf", data: files });
  };

  const handleCandidateUpload = async () => {
    if (!file.data || file.data.length === 0) {
      showSnack("Please upload candidate file(s).");
      return;
    }

    setUploading(true);
    const totalFiles = file.data.length;
    setUploadProgress({ current: 0, total: totalFiles });
    // let result;

    try {
      if (file.type === "pdf") {
        for (let i = 0; i < totalFiles; i++) {
          setUploadProgress({ current: i + 1, total: totalFiles });
          const singleFileArray = [file.data[i]];
          const result = await dispatch(
            uploadPdfCandidateFile(jobId, singleFileArray)
          );

          if (!result?.success) {
            showSnack(`Failed at file ${i + 1}: ${result.message}`, "error");
            break;
          }
        }

        showSnack("PDF files uploaded successfully", "success");
      } else if (file.type === "csv") {
        setUploadProgress({ current: 1, total: 1 });
        const result = await dispatch(
          uploadCsvCandidateFile(jobId, file.data[0])
        );
        if (result?.success) {
          showSnack(result.message, "success");
        } else {
          showSnack(result.message || "CSV upload failed", "error");
        }
      } else {
        showSnack("Only CSV and PDF files are supported.");
        return;
      }

      setShowUploadModal(false);
      setFile({ type: "", data: [] });
      dispatch(fetchCandidatesByJobId(jobId));
    } catch (err) {
      showSnack(err?.message || "Unexpected error occurred", "error");
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  return (
    <div className="job-tabs">
      <div className="job-navigation">
        <div className="d-flex align-items-center" style={{ gap: "8px" }}>
          <img
            src="/images/back-arrow-black.svg"
            alt=""
            onClick={() => navigate("/jobs")}
            style={{ cursor: "pointer" }}
          />
          {titleLoading ? (
            <div
              className="loading-overlay"
              style={{
                alignItems: "start",
                height: "auto",
                display: "flex",
                justifyContent: "start",
              }}
            >
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading job...</span>
              </div>
            </div>
          ) : (
            <h3>{job ? job.title : ""}</h3>
          )}
        </div>
        <button
            className="add-candidate-btn"
            onClick={() => setShowUploadModal(true)}
          >
            Add Candidates
          </button>
      </div>
      <div className="tabs-header">
        <div className="d-flex tabs-items">
          <NavLink to={`/jobs/${jobId}/candidates`} className="tab-btn">
            Candidates
          </NavLink>
          <NavLink to={`/jobs/${jobId}/details`} className="tab-btn">
            Job Details
          </NavLink>
          <NavLink to={`/jobs/${jobId}/settings`} className="tab-btn">
            Job Settings
          </NavLink>
          <NavLink to={`/jobs/${jobId}/automation`} className="tab-btn">
            Automation
          </NavLink>
        </div>
      </div>

      {/* Render Active Tab */}
      <div className="tab-content">
        <Outlet />
      </div>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={snackOpen}
        autoHideDuration={3000}
        onClose={snackClosed}
      >
        <Alert
          onClose={snackClosed}
          severity={snackSeverity}
          sx={{ width: "100%" }}
        >
          {snackMessage}
        </Alert>
      </Snackbar>

      {showUploadModal === true ? (
        <UploadCandidateModal
          show={showUploadModal}
          uploading={uploading}
          file={file}
          uploadProgress={uploadProgress}
          csvInputRef={csvInputRef}
          pdfInputRef={pdfInputRef}
          handleCsvClick={handleCsvClick}
          handlePdfClick={handlePdfClick}
          handleCsvUpload={handleCsvUpload}
          handlePdfUpload={handlePdfUpload}
          handleCandidateUpload={handleCandidateUpload}
          resetUploadModal={resetUploadModal}
          downloadSampleCsv={downloadSampleCsv}
        />
      ) : null}
    </div>
  );
};

export default JobTabs;
